import React, { useEffect, useRef, useState } from "react";

import api from "../services/api";
import { getAvecCache } from "../services/horsConnexion";
import { BoutonHorsConnexion, useFichierHorsConnexion } from "./HorsConnexion";
import { ActivityStatus } from "./LearningProgress";

export function duree(secondes) {
  const s = Math.max(0, Math.floor(secondes || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = String(s % 60).padStart(2, "0");
  return h ? `${h}:${String(m).padStart(2, "0")}:${r}` : `${m}:${r}`;
}

function minutes(secondes) {
  if (!secondes) return null;
  return secondes < 60 ? `${secondes} s` : `${Math.round(secondes / 60)} min`;
}

function LecteurAudio({ audio, onFermer, onProgression }) {
  const element = useRef(null);
  const minuteur = useRef(null);
  const [lecture, setLecture] = useState(false);
  const [position, setPosition] = useState(0);
  const [total, setTotal] = useState(audio.duree_secondes || 0);
  const reprise = audio.progression && !audio.progression.termine ? audio.progression.position_seconds : 0;
  const [choix, setChoix] = useState(reprise > 5 ? "a_choisir" : "pret");
  // Cours gardé sur l'appareil : lu sans réseau.
  const local = useFichierHorsConnexion(`audio:${audio.id}`);

  const enregistrer = (rafraichir) => {
    const a = element.current;
    if (!a || !a.duration || !isFinite(a.duration)) return;
    api.post(`audios/${audio.id}/progression/`, { position: Math.round(a.currentTime), duree: Math.round(a.duration) })
      .then(() => { if (rafraichir) onProgression?.(); })
      .catch(() => {});
  };

  useEffect(() => () => {
    clearInterval(minuteur.current);
    enregistrer(true);
    // Enregistre la position au moment de quitter le lecteur.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const demarrer = (depuis) => {
    const a = element.current;
    setChoix("pret");
    if (!a) return;
    a.currentTime = depuis;
    a.play().catch(() => {});
  };

  const basculer = () => {
    const a = element.current;
    if (!a) return;
    if (a.paused) a.play().catch(() => {}); else a.pause();
  };

  const decaler = (secondes) => {
    const a = element.current;
    if (a) a.currentTime = Math.min(Math.max(0, a.currentTime + secondes), a.duration || a.currentTime);
  };

  return (
    <div className="ql-audio-lecteur" style={audio.photo_url ? { backgroundImage: `url(${audio.photo_url})` } : undefined}>
      <div className="ql-audio-voile">
        <div className="ql-audio-entete">
          <button type="button" className="btn btn-sm btn-light" onClick={onFermer}>
            <i className="bi bi-chevron-left me-1"></i>Cours audio
          </button>
        </div>
        {audio.photo_url
          ? <img className="ql-audio-photo" src={audio.photo_url} alt="" />
          : <div className="ql-audio-photo ql-audio-photo-vide"><i className="bi bi-headphones"></i></div>}
        <h4 className="ql-audio-titre">{audio.title}</h4>
        {audio.module_title && <div className="ql-audio-module">{audio.module_title}</div>}

        <audio
          ref={element}
          src={local?.adresse || audio.audio_url}
          preload="metadata"
          onLoadedMetadata={(e) => setTotal(e.currentTarget.duration)}
          onTimeUpdate={(e) => setPosition(e.currentTarget.currentTime)}
          onPlay={() => {
            setLecture(true);
            clearInterval(minuteur.current);
            minuteur.current = setInterval(() => enregistrer(false), 10000);
          }}
          onPause={() => { setLecture(false); clearInterval(minuteur.current); enregistrer(true); }}
          onEnded={() => { setLecture(false); clearInterval(minuteur.current); enregistrer(true); }}
        />

        {choix === "a_choisir" ? (
          <div className="ql-audio-reprise">
            <p>Tu t'es arrêté à <strong>{duree(reprise)}</strong>.</p>
            <div className="d-flex gap-2 flex-wrap justify-content-center">
              <button type="button" className="btn btn-primary" onClick={() => demarrer(reprise)}>
                <i className="bi bi-play-fill me-1"></i>Continuer à {duree(reprise)}
              </button>
              <button type="button" className="btn btn-outline-light" onClick={() => demarrer(0)}>
                <i className="bi bi-arrow-counterclockwise me-1"></i>Recommencer
              </button>
            </div>
          </div>
        ) : (
          <div className="ql-audio-commandes">
            <input
              type="range"
              className="form-range ql-audio-barre"
              min="0"
              max={total || 0}
              step="1"
              value={Math.min(position, total || 0)}
              onChange={(e) => { if (element.current) element.current.currentTime = Number(e.target.value); }}
              aria-label="Position de lecture"
            />
            <div className="ql-audio-temps">
              <span>{duree(position)}</span>
              <span>-{duree(Math.max(0, total - position))}</span>
            </div>
            <div className="ql-audio-boutons">
              <button type="button" className="btn btn-outline-light" onClick={() => decaler(-15)} aria-label="Reculer de 15 secondes">
                <i className="bi bi-arrow-counterclockwise"></i> 15
              </button>
              <button type="button" className="btn btn-light ql-audio-play" onClick={basculer} aria-label={lecture ? "Pause" : "Lecture"}>
                <i className={`bi ${lecture ? "bi-pause-fill" : "bi-play-fill"}`}></i>
              </button>
              <button type="button" className="btn btn-outline-light" onClick={() => decaler(15)} aria-label="Avancer de 15 secondes">
                15 <i className="bi bi-arrow-clockwise"></i>
              </button>
            </div>
          </div>
        )}
        <div className="d-flex justify-content-center mt-2">
          <BoutonHorsConnexion cle={`audio:${audio.id}`} url={audio.audio_url} titre={audio.title} type="audio" rubrique="audios"
            details={{ module: audio.module_title }} variante="btn-light" />
        </div>
        {audio.description && <p className="ql-audio-description">{audio.description}</p>}
      </div>
    </div>
  );
}

// Liste des cours audio (tous ceux du manuel actif, ou ceux d'un module).
export default function CoursAudio({ moduleId, onProgression }) {
  const [audios, setAudios] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [ouvert, setOuvert] = useState(null);

  const charger = () => {
    getAvecCache(moduleId ? `audios/?module=${moduleId}` : "audios/")
      .then((r) => setAudios(Array.isArray(r.data) ? r.data : (r.data?.results || [])))
      .catch(() => {})
      .finally(() => setChargement(false));
  };
  useEffect(charger, [moduleId]);

  if (ouvert) {
    return (
      <LecteurAudio
        audio={ouvert}
        onFermer={() => { setOuvert(null); charger(); }}
        onProgression={onProgression}
      />
    );
  }
  if (chargement) return <div className="text-center py-5 text-muted"><div className="spinner-border spinner-border-sm me-2"></div>Chargement...</div>;
  if (!audios.length) return <div className="text-center py-5 text-muted"><i className="bi bi-headphones fs-1 d-block mb-2"></i>Aucun cours audio pour le moment.</div>;

  return (
    <div className="ql-audio-liste">
      {audios.map((audio) => (
        <button type="button" key={audio.id} className="ql-audio-carte" onClick={() => setOuvert(audio)}>
          {audio.photo_url
            ? <img src={audio.photo_url} alt="" className="ql-audio-vignette" />
            : <span className="ql-audio-vignette ql-audio-photo-vide"><i className="bi bi-headphones"></i></span>}
          <span className="ql-audio-infos">
            <strong>{audio.title}</strong>
            {!moduleId && audio.module_title && <small>{audio.module_title}</small>}
            {minutes(audio.duree_secondes) && <small><i className="bi bi-clock me-1"></i>{minutes(audio.duree_secondes)}</small>}
            <ActivityStatus kind="audio" id={audio.id} />
          </span>
          <span className="btn btn-sm btn-primary flex-shrink-0">
            <i className="bi bi-play-fill me-1"></i><ActivityStatus kind="audio" id={audio.id} action fallback="Écouter" />
          </span>
        </button>
      ))}
    </div>
  );
}
